import "./ArtistNetwork.scss";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Col,
  Row,
  Form,
  Select,
  Slider,
  InputNumber,
  Typography,
  Popover,
  Table,
  Tag,
  Radio,
  Tooltip,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import { fetchArtistGraph } from "radar/Service/RadarService";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Card, GraphNetwork } from "shared/components";
import {
  RADAR_ARTIST_RANKING_FILTER,
  RADAR_ARTIST_RANKING_INDEX,
} from "shared/constants/LocalStorageConsts";
import { getI18nGenre } from "shared/helpers/i18nGenre";
import moment from "moment";
import { getI18nCoutry } from "shared/helpers/i18nCountry";
import RadioButtonRankingOptions from "../radio-button-ranking-options/RadioButtonRankingOptions";
import { ptBRFormat } from "shared/helpers/BRLFormat";
import { ArtistsRankingIndex } from "explorer/reducers/rankings";
import {
  dataLayerViewedEvent,
  tagManagerEvent,
} from "event-tracker/gtm-events";
import { UserTypeEnum } from "shared/enums/UserTypeEnum";
import { filterOptionDiacritics } from "shared/components/single-multiselect/SingleMultiselect";

const defaults = {
  defaultForm: {
    country: [],
    genre: [],
    genreSpotify: [],
    stage: [1, 10],
    daysSinceFirstRelease: null,
    daysWithoutRelease: null,
    releasesLastYear: "",
  },
  defaultFilter: {
    country: [],
    genre: [],
    genreSpotify: [],
    stageInit: 1,
    stageEnd: 10,
    offset: 0,
    limit: 10,
  },
};

const initial = () => {
  const savedFilter = localStorage.getItem(RADAR_ARTIST_RANKING_FILTER);

  if (savedFilter) {
    const parsed = JSON.parse(savedFilter);

    const { stageInit = 1, stageEnd = 10, ...rest } = parsed;
    return {
      defaultForm: {
        ...rest,
        stage: [stageInit, stageEnd],
      },
      defaultFilter: parsed,
    };
  }

  return defaults;
};

const formatDate = (date: string) => date && moment(date).format("DD/MM/YYYY");

const LIMIT_ARTIST_GRAPH = 2000;

const ArtistNetwork: React.FC<any> = (props) => {
  const { defaultFilter, defaultForm } = initial();
  const savedIndex = localStorage.getItem(
    RADAR_ARTIST_RANKING_INDEX
  ) as ArtistsRankingIndex;
  const { history } = props;
  const [filter, setFilter] = useState<any>(defaultFilter);
  const [genreGraphOptions, setGenreGraphOptions] = useState<any>([]);
  const [artistGraphOptions, setArtistGraphOptions] = useState<any>([]);
  const [spotifyGenresRanking, setSpotifyGenresRanking] = useState<any>([]);
  const [genreSpotifyOptions, setGenreSpotifyOptions] = useState<any[]>([]);
  const [spotifyGenreSearch, setSpotifyGenreSearch] = useState<string>("");
  const [rawGraph, setRawGraph] = useState<any>();
  const [graphOptionsSelected, setGraphOptionsSelected] = useState<string>("");
  const [loadingGraph, setLoadingGraph] = useState<boolean>(true);
  const [mountGraph, setMountGraph] = useState<boolean>(false);
  const [mountLinks, setMountLinks] = useState<boolean>(false);
  const [proccessedGraph, setProccessedGraph] = useState<any>();
  const [rankingIndex, setRankingIndex] = useState<ArtistsRankingIndex>(
    savedIndex ? savedIndex : ArtistsRankingIndex.Performance
  );
  const [artistFilter, setArtistFilter] = useState("");
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation("radar");

  const { genres, genreOptions } = useSelector((state: any) => state.genres);
  const { countries, countryOptions } = useSelector(
    (state: any) => state.countries
  );
  const genresSpotify = useSelector((state: any) => state.genres.genresSpotify);

  const loggedUser = useSelector((state: any) => state.users.loggedUser);
  const admin = loggedUser.typeAccess === UserTypeEnum.AdminStrm;

  const wrappGetI18nGenre = (genre: string) =>
    getI18nGenre(genre, i18n, genres);

  const wrappGetI18nCountry = (country: string) =>
    getI18nCoutry(country, i18n, countries);

  const daysOptions = [
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 1 }),
      value: 365,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 2 }),
      value: 730,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 3 }),
      value: 1095,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 4 }),
      value: 1460,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 5 }),
      value: 1825,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 10 }),
      value: 3650,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 15 }),
      value: 5475,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 20 }),
      value: 7300,
    },
    {
      label: t("pages.ArtistsRanking.yearsSinceRelease", { year: 30 }),
      value: 10950,
    },
  ];

  const daysWithoutReleaseOptions = [
    { label: 1, value: 365 },
    { label: 2, value: 730 },
    { label: 3, value: 1095 },
    { label: 4, value: 1460 },
    { label: 5, value: 1825 },
    { label: 10, value: 3650 },
    { label: 15, value: 5475 },
    { label: 20, value: 7300 },
    { label: 30, value: 10950 },
  ];

  const colunasGeneros: ColumnsType<any> = [
    {
      title: t("pages.ArtistsRanking.genre"),
      render: (value: any) => {
        return (
          <span style={{ color: `${value.color}` }}>{value.genre_spotify}</span>
        );
      },
    },
    {
      title: t("pages.ArtistsRanking.percentage"),
      dataIndex: "percent",
      align: "center",
      render: (value: any) => <>{value} %</>,
    },
    {
      title: t("pages.ArtistsRanking.quantity"),
      align: "center",
      dataIndex: "size",
    },
  ];
  const colunas: ColumnsType<any> = [
    {
      dataIndex: "position",
      width: "5%",
      render: (value) => <>{value ? value : "N/A"}</>,
    },
    {
      title: t("pages.ArtistsRanking.artistName"),
      dataIndex: "artistName",
      width: "15%",
    },
    {
      title: t("pages.ArtistsRanking.genres"),
      dataIndex: "genresStrm",
      width: "15%",
      render: (value) =>
        value.map((genre: any) => (
          <Tag key={Math.random()}>{wrappGetI18nGenre(genre)}</Tag>
        )),
    },
    {
      title: t("pages.ArtistsRanking.country"),
      dataIndex: "country",
      align: "center",
      width: "10%",
      render: (value: string) => <>{wrappGetI18nCountry(value)}</>,
    },
    {
      title: t("pages.ArtistsRanking.stage"),
      dataIndex: "careerStage",
      align: "center",
      width: "10%",
    },
    {
      title: t("pages.ArtistsRanking.development"),
      dataIndex: "developmentIndex",
      render: (value) => <>{(value || value === 0) && value.toPrecision(3)}</>,
      align: "center",
      width: "10%",
    },
    {
      title: t("pages.ArtistsRanking.performance"),
      dataIndex: "performanceIndex",
      render: (value) => <>{(value || value === 0) && value.toPrecision(3)}</>,
      align: "center",
      width: "10%",
    },
  ];

  if (admin) {
    colunas.push({
      key: "acao",
      width: "5%",
      align: "right",
      render: (_, record) => (
        <a
          href={`/artist-profile/${record.artistIdStrm}`}
          style={{ color: "white" }}
          target="_blank"
          rel="noreferrer"
          onClick={(evt) => evt.stopPropagation()}
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} />
        </a>
      ),
    });
  }

  const onSelectSpotifyGenre = (value: any) => {
    if (value === "all") {
      if (!spotifyGenreSearch) form.setFieldsValue({ genreSpotify: [] });
      else {
        const filteredGenres = genresSpotify.filter((genre: any = "") =>
          genre.includes(spotifyGenreSearch)
        );
        form.setFieldsValue({ genreSpotify: filteredGenres });
      }
    }
  };

  const onDeleteClick = () => {
    form.setFieldsValue(defaults.defaultForm);
    setFilter(defaults.defaultFilter);
  };

  const onFinishFilter = () => {
    const {
      country,
      daysSinceFirstRelease,
      genre,
      genreSpotify,
      releasesLastYear,
      stage,
    } = form.getFieldsValue(true);
    const [stageInit, stageEnd] = stage;
    const saveFilter = {
      ...defaultFilter,
      country: country?.length === 0 ? undefined : country,
      daysSinceFirstRelease,
      genre,
      genreSpotify,
      releasesLastYear,
      stageInit,
      stageEnd,
    };
    localStorage.setItem(
      RADAR_ARTIST_RANKING_FILTER,
      JSON.stringify(saveFilter)
    );
    setFilter(saveFilter);
  };

  const onChangeIndex = (evt: any) => {
    const saveRankingIndex = evt.target.value;
    localStorage.setItem(RADAR_ARTIST_RANKING_INDEX, saveRankingIndex);
    setRankingIndex(saveRankingIndex);
  };

  const safeCompare = (first: string, second: string) => {
    return second && first?.toLowerCase() === second?.toLowerCase();
  };

  const onChangeHightlightArtist = (value: string) => {
    setProccessedGraph(undefined);
    setLoadingGraph(true);
    setArtistFilter(value);
  };

  useEffect(() => {
    if (rawGraph) {
      setLoadingGraph(true);
      setSpotifyGenresRanking(rawGraph?.genres_spotify || []);
      setGenreGraphOptions(
        rawGraph?.genres_spotify?.map(({ genre_spotify }: any) => ({
          id: genre_spotify,
          value: genre_spotify,
        })) || []
      );
      setProccessedGraph({
        nodes:
          Object.keys(rawGraph?.nodes || []).map((key: string) => ({
            ...rawGraph.nodes[key],
            id: key,
            color:
              rawGraph.nodes[key].genres_spotify?.includes(
                graphOptionsSelected
              ),
            highLight: safeCompare(
              rawGraph.nodes[key].artist_name,
              artistFilter
            ),
          })) || [],
        links:
          rawGraph.edges?.map(([source, target]: [any, any]) => ({
            source,
            target,
          })) || [],
      });
      setArtistGraphOptions(
        Object.keys(rawGraph?.nodes || []).map(
          (key: string) => rawGraph.nodes[key].artist_name
        )
      );
      setLoadingGraph(false);
    }
  }, [rawGraph, artistFilter, graphOptionsSelected]);

  const onChangeGraphOptions = (value: string) => {
    setProccessedGraph(undefined);
    setLoadingGraph(true);
    setGraphOptionsSelected(value);
  };

  const includeArtist = (nodeArtists: any) => {
    const mapArtistsName =
      nodeArtists?.map(({ artist_name }: any) => {
        return artist_name.toLocaleLowerCase();
      }) || [];
    const result = mapArtistsName.includes(artistFilter.toLocaleLowerCase());
    return result;
  };

  useEffect(() => {
    if (graphOptionsSelected || artistFilter) setMountLinks(true);
  }, [graphOptionsSelected, artistFilter]);

  useEffect(() => {
    const getArtistNetworkGraph = async (limit: number) => {
      setLoadingGraph(true);
      const result = await fetchArtistGraph(
        {
          ...filter,
          limit: limit,
          offset: 0,
        },
        rankingIndex
      );
      setRawGraph(result);
      setMountGraph(true);
      setLoadingGraph(false);
    };

    getArtistNetworkGraph(LIMIT_ARTIST_GRAPH);
  }, [filter, rankingIndex]);

  useEffect(() => {
    dataLayerViewedEvent({
      ...tagManagerEvent.Ranking.S4L_ARTIST_NETWORK,
    });
  }, []);

  useEffect(() => {
    setGenreSpotifyOptions(
      genresSpotify
        .map((genre: any) => ({
          label: genre,
          value: genre,
        }))
        .sort((a: any, b: any) =>
          a.label > b.label ? 1 : a.label < b.label ? -1 : 0
        )
    );
  }, [genresSpotify]);

  const Node = ({ node }: any) => {
    const content = () => (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <div>
          {t("pages.ArtistsRanking.artistNameCard")}
          <b>{node.artist_name}</b>
        </div>
        <div>
          {t("pages.ArtistsRanking.stageCard")}
          <b>{node.career_stage}</b>
        </div>
        <div>
          {t("pages.ArtistsRanking.spotifyGenreCard")}
          {node.genres_spotify?.map((genre: any) => (
            <Tag
              key={`genreSpiitify_${node.artist_id_strm}_${genre}`}
              color="transparent"
              style={{ border: "1px solid #8AE79A" }}
            >
              {wrappGetI18nGenre(genre)}
            </Tag>
          ))}
        </div>
        <div>
          {t("pages.ArtistsRanking.strmGenreCard")}
          {node.genres_strm?.map((genre: any) => (
            <Tag
              key={`STRMGenreCard${node.artist_id_strm}_${genre}`}
              color="transparent"
              style={{ border: "1px solid #7FA2FC" }}
            >
              {wrappGetI18nGenre(genre)}
            </Tag>
          ))}
        </div>
        <div>
          <Row
            hidden={!node.artistNodeEdges}
            style={{ flexDirection: "column" }}
          >
            <Col xs={24}>
              <b>{t("pages.ArtistsRanking.relation")} </b>
            </Col>
            {node.artistNodeEdges?.map((edge: any) => (
              <Col
                key={`edge${edge.artist_id_strm}_${node.artist_id_strm}`}
                style={{ margin: 2 }}
              >
                {`${edge.artist_name} - ${edge.career_stage}:  `}
                {edge.genres_spotify?.map((genre: any) => (
                  <Tag
                    key={`spotify_genre_${edge.artist_id_strm}_${genre}_${node.artist_id_strm}`}
                    color="transparent"
                    style={{ border: "1px solid #8AE79A" }}
                  >
                    {wrappGetI18nGenre(genre)}
                  </Tag>
                ))}
                {edge.genres_strm?.map((genre: any) => (
                  <Tag
                    key={`genre_STRM_${edge.artist_id_strm}_${genre}_${node.artist_id_strm}`}
                    color="transparent"
                    style={{ border: "1px solid #7FA2FC" }}
                  >
                    {wrappGetI18nGenre(genre)}
                  </Tag>
                ))}
              </Col>
            ))}
          </Row>
        </div>
      </div>
    );

    return (
      <>
        <Popover content={content} trigger="hover">
          <circle
            onClick={() => {
              setArtistFilter(node.artist_name);
            }}
            fill={
              node.highLight
                ? "#C52831"
                : includeArtist(node.artistNodeEdges)
                ? "#7FA2FC"
                : node.color
                ? "#8AE79A"
                : `#EFEFEF`
            }
            stroke={"#696969"}
            r={node.career_stage}
          />
        </Popover>
      </>
    );
  };

  const Line = ({ link, ...rest }: any) => {
    return (
      <line
        stroke={
          safeCompare(rawGraph.nodes[link.source]?.artist_name, artistFilter) ||
          safeCompare(rawGraph.nodes[link.target]?.artist_name, artistFilter)
            ? "#FF8CF4"
            : "grey"
        }
        {...rest}
      />
    );
  };

  return (
    <div className="radar-home" style={{ color: "white" }}>
      <Row style={{ marginTop: "16px" }}>
        <Col style={{ textAlign: "center" }}>
          <h1 className="ranking-title">{t("pages.ArtistsRanking.title")}</h1>
        </Col>
      </Row>
      <Row style={{ marginTop: "16px" }}>
        <Col style={{ textAlign: "center" }}>
          <RadioButtonRankingOptions value="artist" />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} className="conteudo-home">
          <Form
            form={form}
            initialValues={defaultForm}
            onFinish={onFinishFilter}
            className="filtro"
            layout="vertical"
          >
            <Card>
              <Row gutter={24}>
                <Col xs={5}>
                  <Form.Item
                    name="genre"
                    label={t("pages.ArtistsRanking.genre")}
                  >
                    <Select
                      mode="multiple"
                      options={genreOptions}
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                      filterOption={filterOptionDiacritics}
                    />
                  </Form.Item>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="genreSpotify"
                    label={t("pages.ArtistsRanking.genreSpotify")}
                  >
                    <Select
                      mode="multiple"
                      filterOption={(input, option) =>
                        (option?.value as string)
                          .toUpperCase()
                          .includes(input.toUpperCase()) ||
                        option?.value === "all"
                      }
                      onSearch={(value) => setSpotifyGenreSearch(value)}
                      onDropdownVisibleChange={(open) =>
                        setSpotifyGenreSearch("")
                      }
                      onSelect={onSelectSpotifyGenre}
                      maxTagCount="responsive"
                      allowClear
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    >
                      <Select.Option key="all" value="all">
                        {t("pages.ArtistsRanking.selectAll")}
                      </Select.Option>
                      {genreSpotifyOptions.map((option: any) => (
                        <Select.Option value={option.value}>
                          {option.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="country"
                    label={t("pages.ArtistsRanking.country")}
                  >
                    <Select
                      mode="multiple"
                      options={countryOptions}
                      filterOption={(input, option) =>
                        (option?.label as string)
                          .toUpperCase()
                          .includes(input.toUpperCase())
                      }
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="stage"
                    label={t("pages.ArtistsRanking.stage")}
                  >
                    <Slider
                      min={1}
                      max={10}
                      range
                      marks={{
                        1: {
                          style: {
                            color: "#fff",
                          },
                          label: <strong>1</strong>,
                        },
                        10: {
                          style: {
                            color: "#fff",
                          },
                          label: <strong>10</strong>,
                        },
                      }}
                      tooltipPlacement="bottom"
                    />
                  </Form.Item>
                </Col>
                <Col xs={4}>
                  <div className="filter-button ">
                    <span className="delete" onClick={onDeleteClick}>
                      {t("pages.ArtistsRanking.delete")}
                    </span>
                    <button className="apply">
                      {t("pages.ArtistsRanking.filter")}
                    </button>
                  </div>
                </Col>
                <Col xs={5}>
                  <Form.Item
                    name="daysSinceFirstRelease"
                    label={t("pages.ArtistsRanking.daysSinceFirstRelease")}
                  >
                    <Select
                      options={daysOptions}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                      showArrow={false}
                    />
                  </Form.Item>
                </Col>

                <Col xs={5}>
                  <Form.Item
                    name="daysWithoutRelease"
                    label={t("pages.ArtistsRanking.daysWithoutRelease")}
                  >
                    <Select
                      options={daysWithoutReleaseOptions}
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                      showArrow={false}
                    />
                  </Form.Item>
                </Col>

                <Col xs={5}>
                  <Form.Item
                    name="releasesLastYear"
                    label={t("pages.ArtistsRanking.releasesInThePastYear")}
                  >
                    <InputNumber
                      min={0}
                      placeholder={t("pages.ArtistsRanking.typeHere")}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Form>
          <Row style={{ marginTop: 16, marginBottom: 32 }}>
            <Col style={{ textAlign: "center" }}>
              <Radio.Group value="network">
                <Radio.Button
                  value="ranking"
                  onClick={() => history.push("/artist-ranking")}
                >
                  {t("pages.ArtistsRanking.title")}
                </Radio.Button>
                <Radio.Button
                  value="network"
                  onClick={() => history.push("/artist-network")}
                >
                  {t("pages.ArtistsRanking.artistNetwork")}
                </Radio.Button>
              </Radio.Group>
            </Col>
          </Row>

          <Row style={{ marginBottom: 30 }}>
            <Col xs={10}>
              <Row gutter={16}>
                <Col xs={12}>
                  <span
                    style={{
                      display: "block",
                      marginTop: -30,
                      marginBottom: 8,
                    }}
                  >
                    {t("pages.ArtistsRanking.artistHighlight")}
                  </span>
                  <Form.Item>
                    <Select
                      showArrow={false}
                      onClear={() => {
                        onChangeHightlightArtist("");
                      }}
                      optionFilterProp="value"
                      allowClear
                      showSearch
                      onSelect={onChangeHightlightArtist}
                      options={artistGraphOptions?.map((option: any) => {
                        return {
                          label: option,
                          value: option,
                        };
                      })}
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <span
                    style={{
                      display: "block",
                      marginTop: -30,
                      marginBottom: 8,
                    }}
                  >
                    {t("pages.ArtistsRanking.genre")}
                  </span>{" "}
                  <Form.Item>
                    <Select
                      onChange={onChangeGraphOptions}
                      options={genreGraphOptions}
                      placeholder={t("pages.ArtistsRanking.selectHere")}
                      showArrow={false}
                      showSearch
                      allowClear
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Col>
            <Col
              xs={14}
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Radio.Group onChange={onChangeIndex} value={rankingIndex}>
                <Radio.Button value={ArtistsRankingIndex.Performance}>
                  <Tooltip
                    title={t("pages.ArtistsRanking.performanceExplaination")}
                    overlayStyle={{ width: 500 }}
                  >
                    {t("pages.ArtistsRanking.performance")}
                  </Tooltip>
                </Radio.Button>
                <Radio.Button value={ArtistsRankingIndex.DeltaSpotify}>
                  <Tooltip
                    overlayStyle={{ width: 500 }}
                    title={t("pages.ArtistsRanking.deltaSpotifyExplaination")}
                  >
                    {t("pages.ArtistsRanking.spotifyDelta")}
                  </Tooltip>
                </Radio.Button>
                <Radio.Button
                  value={ArtistsRankingIndex.PerformanceDevelopment}
                >
                  <Tooltip
                    overlayStyle={{ width: 500 }}
                    title={t(
                      "pages.ArtistsRanking.performanceDevelopmentExplaination"
                    )}
                  >
                    {t("pages.ArtistsRanking.performanceDevelopment")}
                  </Tooltip>
                </Radio.Button>
                <Radio.Button value={ArtistsRankingIndex.GrowthSpeed}>
                  <Tooltip
                    overlayStyle={{ width: 500 }}
                    title={t("pages.ArtistsRanking.growthSpeedExplaination")}
                  >
                    {t("pages.ArtistsRanking.growthSpeed")}
                  </Tooltip>
                </Radio.Button>
                <Radio.Button value={ArtistsRankingIndex.Volatility}>
                  <Tooltip
                    overlayStyle={{ maxWidth: 550 }}
                    title={t("pages.ArtistsRanking.volatilityExplaination")}
                  >
                    {t("pages.ArtistsRanking.volatility")}
                  </Tooltip>
                </Radio.Button>
              </Radio.Group>
            </Col>
          </Row>
          <Row style={{ marginBottom: 30 }}>
            <Col xs={12}>
              <Typography.Title
                level={4}
                style={{ color: "white", textAlign: "left" }}
              >
                <div className="container-warning">
                  <Typography.Title level={4} className="title">
                    {t("pages.ArtistsRanking.results", {
                      total: ptBRFormat(LIMIT_ARTIST_GRAPH),
                    })}
                  </Typography.Title>
                </div>
              </Typography.Title>
            </Col>
            <Col xs={12}>
              <Typography.Title
                level={5}
                style={{ color: "white", textAlign: "right" }}
              >
                <b>
                  {t("pages.ArtistsRanking.lastUpdate")}
                  <b>{`${formatDate(rawGraph?.ranking_date) || ""} `}</b>
                </b>
              </Typography.Title>
            </Col>
          </Row>
          <Row>
            <Col xs={24}>
              <div className="legenda">
                <span className="caixa" style={{ backgroundColor: "#EFEFEF" }}>
                  &nbsp;&nbsp;&nbsp;&nbsp;
                </span>
                <span className="label">
                  {t("pages.ArtistsRanking.normalNode")}
                </span>

                <span className="caixa" style={{ backgroundColor: "#C52831" }}>
                  &nbsp;&nbsp;&nbsp;&nbsp;
                </span>
                <span className="label">
                  {t("pages.ArtistsRanking.highlightedNode")}
                </span>

                <span className="caixa" style={{ backgroundColor: "#7FA2FC" }}>
                  &nbsp;&nbsp;&nbsp;&nbsp;
                </span>
                <span className="label">
                  {t("pages.ArtistsRanking.relatedSelectedNodes")}
                </span>

                <span className="caixa" style={{ backgroundColor: "#8AE79A" }}>
                  &nbsp;&nbsp;&nbsp;&nbsp;
                </span>
                <span className="label">
                  {t("pages.ArtistsRanking.selectedGroupNode")}
                </span>
              </div>
            </Col>
            <Col xs={24}>
              <div style={{ height: "100vh" }}>
                <GraphNetwork
                  data={proccessedGraph}
                  loading={loadingGraph}
                  mountGraph={mountGraph}
                  mountLinks={mountLinks}
                  setMountGraph={setMountGraph}
                  setMountLinks={setMountLinks}
                  id="graph"
                  enableDrag={true}
                  zoomDepth={8}
                  nodeDistance={2}
                  NodeComponent={Node}
                  LineComponent={Line}
                  highLight={artistFilter}
                />
              </div>
            </Col>

            <Col xs={24}>
              <Table
                columns={colunasGeneros}
                dataSource={spotifyGenresRanking || []}
                rowKey="genre_spotify"
                loading={loadingGraph}
                style={{ marginTop: 48 }}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default ArtistNetwork;
